/**
 * The plan prompt, for when someone reaches for something their plan does not
 * include.
 *
 * **Why this is a modal when `upgrade-panel.ts` says panels.** That rule —
 * "only destructive confirmation and sign-in earn a modal" — is about the
 * *navigational* case: you went to the billing page to look at plans, and
 * interrupting someone who is already looking at the thing is rude. This is the
 * other case, and it is the shape the rule already exempts for sign-in: you
 * asked the app to do something, and it cannot proceed until a question is
 * answered. The interruption is the answer to a request, not an ambush.
 *
 * **It renders `createUpgradePanel` rather than reimplementing it.** There is
 * one pricing implementation, one interval toggle, one set of benefit copy, one
 * place that knows what Stripe will actually charge. This module is a frame: a
 * reason at the top, an optional offer at the bottom, and a way out. If it grew
 * its own price list, the two would disagree within a month and the one users
 * see least would be the wrong one.
 *
 * **"Not now" is not a courtesy.** A prompt with only a way forward is a trap,
 * and this one fires while somebody is mid-design. It comes free from the
 * panel's own `onClose`, which is why that option exists.
 */
import type { PaidTier } from "../api/billing";
import { el, replace } from "./dom";
import { createUpgradePanel } from "./upgrade-panel";

/**
 * An extra inducement, shown under the plan.
 *
 * Data rather than markup so the frame does not have to know what a referral is,
 * and **absent by default** — nothing renders unless an offer is passed in. A
 * prompt that advertises a trial the billing system cannot honour is worse than
 * one that advertises nothing, and the copy is the easiest half to write.
 */
export interface UpsellOffer {
  headline: string;
  detail?: string;
  action: { label: string; onSelect: () => void };
}

/**
 * The share-for-trial offer, ready to pass and deliberately not passed.
 *
 * Turning this on needs three things that do not exist yet: a referral code per
 * account, a Stripe coupon for the referrer's 30%, and `trial_period_days` on
 * the invited checkout session. `pricing-and-checkout.md` currently configures
 * no trial and no deflection coupon, both on purpose — so wiring the button
 * before the billing side exists would put a promise on screen that the
 * checkout would then break.
 *
 * Pass it once those exist:
 *
 *     openUpsell({ reason, offer: shareForTrialOffer(() => startReferral()) })
 */
export function shareForTrialOffer(onSelect: () => void): UpsellOffer {
  return {
    headline: "Invite a friend, both of you win",
    detail: "They get 7 days free. You get 30% off your next month.",
    action: { label: "Get your invite link", onSelect },
  };
}

export interface UpsellOptions {
  /**
   * What they were trying to do, in their words rather than ours.
   *
   * "Pattern 05 · 1 is part of Creator" beats "Upgrade to Creator": it names the
   * thing they wanted, which is the only reason they are reading this at all.
   */
  reason?: string;
  tier?: PaidTier;
  offer?: UpsellOffer;
  /** Fires for every dismissal — "Not now", Escape, the backdrop, the ✕. */
  onDismiss?: () => void;
}

let current: { dialog: HTMLDialogElement; destroy: () => void } | null = null;

/** Close whatever is open. Safe to call when nothing is. */
export function closeUpsell(): void {
  current?.dialog.close();
}

export function openUpsell(options: UpsellOptions = {}): void {
  // One at a time. Two of these stacked would mean two Stripe buttons on
  // screen, and the second would be about a plan nobody asked for.
  closeUpsell();
  /*
    Sweep any that outlived their close.

    `HTMLDialogElement.close()` fires its event in a queued task, not
    synchronously, so a dialog closed and reopened in the same turn can leave
    the old element in the document — and a stale `dialog.upsell` in the DOM is
    worse than untidy: `querySelector` finds *it* rather than the live one, so
    anything asking whether the prompt is open gets the wrong answer.
  */
  for (const stale of document.querySelectorAll("dialog.upsell")) stale.remove();

  const dialog = el("dialog", { class: "upsell floating", aria: { label: "Plans" } });

  const close = el("button", {
    class: "icon-btn upsell__close",
    type: "button",
    aria: { label: "Close" },
    text: "✕",
  });
  /*
    Explicit teardown, called by every exit.

    `close` is not dependable: measured directly, a plain <dialog> here opens
    with showModal() and never fires it. Declared before the panel because the
    panel's "Not now" needs to call it, and idempotent because several paths
    can reach it — removing a removed node and nulling a null are both no-ops.
  */
  let panel: { el: HTMLElement; destroy(): void } | null = null;
  const dismiss = () => {
    dialog.remove();
    if (current?.dialog === dialog) current = null;
    try {
      panel?.destroy();
    } catch {
      /* going away with the dialog either way */
    }
    options.onDismiss?.();
  };
  close.addEventListener("click", () => {
    dialog.close();
    dismiss();
  });

  panel = createUpgradePanel({
    tier: options.tier,
    // Gives us "Not now" inside the panel, beside the price, where a decision
    // is actually being made — rather than as a lonely link under everything.
    onClose: () => {
      dialog.close();
      dismiss();
    },
  });

  replace(
    dialog,
    el(
      "div",
      { class: "upsell__head" },
      el(
        "div",
        { class: "upsell__title" },
        lockBadge(),
        el("p", {
          class: "upsell__reason",
          text: options.reason ?? "This is part of Creator",
        }),
      ),
      close,
    ),
    el("div", { class: "upsell__body" }, panel.el),
    options.offer ? offerBlock(options.offer) : null,
  );

  dialog.addEventListener("click", (e) => {
    if (e.target !== dialog) return;
    dialog.close();
    dismiss();
  });
  dialog.addEventListener("cancel", () => dismiss()); // Escape
  // One exit path for every way out, so nothing can dismiss without cleaning up.
  /*
    Still listened for, because where it fires it is the most complete signal —
    but measured directly, a plain <dialog> here opens with showModal() and
    never fires `close`, so every explicit exit calls `dismiss()` too. It is
    idempotent: removing a removed node and nulling a null are both no-ops.
  */
  dialog.addEventListener("close", () => dismiss());

  document.body.appendChild(dialog);
  current = { dialog, destroy: () => panel.destroy() };
  dialog.showModal();
}

/**
 * The padlock beside the heading.
 *
 * The same `i-locked` glyph as the badge on the swatch they just clicked, so
 * the prompt is visibly the answer to that click rather than an advert that
 * happened to arrive. Built here rather than in the shared panel because it
 * belongs to this frame — the pricing page has no lock to explain.
 */
function lockBadge(): HTMLElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "icon");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("href", "#i-locked");
  svg.appendChild(use);
  const badge = el("div", { class: "upsell__badge" });
  badge.appendChild(svg);
  return badge;
}

function offerBlock(offer: UpsellOffer): HTMLElement {
  const button = el("button", {
    class: "upsell__offer-action",
    type: "button",
    text: offer.action.label,
  });
  button.addEventListener("click", () => offer.action.onSelect());
  return el(
    "div",
    { class: "upsell__offer" },
    el(
      "div",
      { class: "upsell__offer-text" },
      el("p", { class: "upsell__offer-headline", text: offer.headline }),
      offer.detail ? el("p", { class: "upsell__offer-detail", text: offer.detail }) : null,
    ),
    button,
  );
}
